import React from "react";
import { Box, Flex, Grid, GridItem, Image, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import StatusBadge from "@/shared/components/StatusBadge";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { PathPickProduct } from "@/features/pathpick/types";
import PathPickAiImageBadge from "@/features/pathpick/components/product/PathPickAiImageBadge";
import {
  getMarketPriceByBasis,
  getPathPickRequestSlot,
  getProductImageUrl,
  isProductImageAiCollected,
  getProductTotalStock,
} from "@/features/pathpick/utils";
import usePathPickProductOptionsHoverPrefetch from "@/features/pathpick/hooks/query/usePathPickProductOptionsHoverPrefetch";

interface ProductListItemProps {
  item: PathPickProduct;
  isSelected: boolean;
  onToggleSelect: () => void;
  onOpenSizeStock: () => void;
  onPick: () => void;
}

const PathPickSearchProductsProductListItem = ({
  item,
  isSelected,
  onToggleSelect,
  onOpenSizeStock,
  onPick,
}: ProductListItemProps) => {
  const { t, tNumberString, tCurrencyString } = useI18nHelper();
  const sizeStockHoverProps = usePathPickProductOptionsHoverPrefetch(item);

  const formatKrw = (value?: number | null) =>
    value != null
      ? tCurrencyString(value, { type: "SYMBOL", code: "KRW" })
      : "-";

  // 리스트 뷰는 기준 선택 없이 KREAM 최저 입찰가만 표시한다(뷰 전환 UI가 꺼져 있어 도달하지 않는다).
  const kreamValue = getMarketPriceByBasis(item.kream?.price, "bidPrice");
  // 예상마진 = KREAM 가격 − 가격(예상 공급가), 마진율 = 예상마진/가격 × 100
  const expectedMargin =
    kreamValue != null ? kreamValue - item.salePrice.value : undefined;
  const marginRate =
    expectedMargin != null && item.salePrice.value > 0
      ? (expectedMargin / item.salePrice.value) * 100
      : undefined;

  // 리스트 뷰는 네이버 엑셀 파일 생성 완료만 "다운로드 완료"로 표시한다(카드 뷰의 배지 전체는 아니다).
  const isNaverExcelReady = Boolean(
    getPathPickRequestSlot(item.requests, "NAVER_EXCEL")?.isCompleted
  );

  return (
    <Grid
      templateColumns={
        "auto auto 68px minmax(220px, 1fr) 120px 120px 130px 90px auto"
      }
      gap={"16px"}
      alignItems={"center"}
      padding={"8px"}
      border={"0.5px solid #C0C0C0"}
      borderRadius={"8px"}
      cursor={"pointer"}
      _hover={{ background: "#F7F9FA" }}
    >
      <GridItem>
        <AppCheckBox
          size={"small"}
          isChecked={isSelected}
          isDisabled={item.isInBasket}
          onChange={onToggleSelect}
        />
      </GridItem>
      <GridItem>
        <AppButton
          variant={"white"}
          isDisabled={item.isInBasket}
          onClick={onPick}
          chakraProps={{
            gap: "4px",
            padding: "4px 8px",
          }}
        >
          <Flex gap={"4px"} alignItems={"center"}>
            <CustomIcon
              src={"package"}
              width={16}
              height={16}
              color={"#444440"}
            />
            <Text textStyle={"body-sm-bold"}>
              {item.isInBasket
                ? t("path_pick:search_products.product_list.picked")
                : t("path_pick:search_products.product_list.pick")}
            </Text>
          </Flex>
        </AppButton>
      </GridItem>
      <GridItem>
        <Box position={"relative"} width={"68px"}>
          <Image
            src={getProductImageUrl(item)}
            fallbackSrc={"/icons-v1_1/null.png"}
            boxSize={"68px"}
            objectFit={"contain"}
            background={"#FFFFFF"}
            border={"0.5px solid #D4D4D1"}
            borderRadius={"8px"}
            alt={item.name}
          />
          {isProductImageAiCollected(item) && (
            <PathPickAiImageBadge size={"small"} />
          )}
        </Box>
      </GridItem>
      <GridItem>
        <Flex flexDirection={"column"} gap={"4px"}>
          <Text textStyle={"body-md-bold"} color={"#444440"}>
            {`[${item.brandName}] ${item.name}`}
          </Text>
          <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
            {`${item.sku} · ${item.categories.join(" / ")} · ${item.color}`}
          </Text>
          <Flex gap={"4px"}>
            {item.isInBasket && (
              <StatusBadge
                status={t("path_pick:search_products.product_list.picked")}
                colorVariant={"gray"}
                size={"small"}
              />
            )}
            {isNaverExcelReady && (
              <StatusBadge
                status={t(
                  "path_pick:search_products.product_list.download_complete"
                )}
                colorVariant={"green"}
                size={"small"}
              />
            )}
          </Flex>
        </Flex>
      </GridItem>
      <GridItem>
        <MetricColumn
          label={t("path_pick:search_products.product_list.supply_price")}
          value={formatKrw(item.salePrice.value)}
        />
      </GridItem>
      <GridItem>
        <MetricColumn
          label={"KREAM"}
          value={formatKrw(kreamValue)}
          valueColor={"#1272EF"}
        />
      </GridItem>
      <GridItem>
        <MetricColumn
          label={t("path_pick:search_products.product_list.expected_margin")}
          value={formatKrw(expectedMargin)}
          sub={marginRate != null ? `${marginRate.toFixed(1)}%` : "-"}
          valueColor={"#16B41C"}
        />
      </GridItem>
      <GridItem>
        <MetricColumn
          label={t("path_pick:search_products.product_list.total_stock")}
          value={`${tNumberString(getProductTotalStock(item))}개`}
        />
      </GridItem>
      <GridItem>
        <Flex
          gap={"2px"}
          alignItems={"center"}
          cursor={"pointer"}
          width={"fit-content"}
          onClick={(event) => {
            event.stopPropagation();
            onOpenSizeStock();
          }}
          {...sizeStockHoverProps}
          color={"#444440"}
          _hover={{ color: "#1272EF" }}
        >
          <Text textStyle={"body-sm-medium"}>
            {t("path_pick:search_products.product_list.size_quantity")}
          </Text>
          <CustomIcon src={"chevron-right"} width={20} height={20} />
        </Flex>
      </GridItem>
    </Grid>
  );
};

export default PathPickSearchProductsProductListItem;

interface MetricColumnProps {
  label: string;
  value: string;
  sub?: string;
  valueColor?: string;
}

const MetricColumn = ({
  label,
  value,
  sub,
  valueColor = "#444440",
}: MetricColumnProps) => {
  return (
    <Flex flexDirection={"column"} gap={"2px"}>
      <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
        {label}
      </Text>
      <Text textStyle={"body-sm-bold"} color={valueColor}>
        {value}
      </Text>
      {sub && (
        <Text textStyle={"body-sm-bold"} color={valueColor}>
          {sub}
        </Text>
      )}
    </Flex>
  );
};
